import { DialogComponent, DialogCloseEvent } from '@theme/dialog';

/**
 * @typedef {object} StoreConfig
 * @property {string} code ISO country / store code from `data-code`, e.g. `"us"`.
 * @property {string} url Absolute storefront URL from the selector item `href`.
 * @property {boolean} current Whether this item is the store the visitor is on.
 */

/**
 * @typedef {object} ModalCopy
 * @property {string} title
 * @property {string} message
 * @property {string} cancel
 * @property {string} change
 * @property {Record<string, string>} stores
 */

/**
 * Bundled modal copy by locale. Shopify `| t` can only emit the current
 * storefront language, so other locales live here and are selected from the
 * detected IP country. Mirrors `wuu_country_modal` in theme locale files.
 *
 * @type {Record<string, ModalCopy>}
 */
const WUU_COUNTRY_MODAL_I18N = {
  'en': {
    title: 'Hi, welcome to WUU 👋',
    message:
      "It looks like you're in {{ country }}.\n\nSwitch to the {{ store }} store?\nWe'll show prices, shipping, and products available in your region.",
    cancel: 'Stay on current site',
    change: 'Go to {{ store }} store',
    stores: {
      tw: 'Taiwan',
      us: 'United States',
    },
  },
  'zh-TW': {
    title: '嗨，歡迎來到無界選物 👋',
    message: '看起來你目前位於 {{ country }}\n\n要切換至{{ store }}站嗎？\n我們會為你顯示當地適用的價格、配送與商品資訊。',
    cancel: '留在目前站點',
    change: '前往{{ store }}站',
    stores: {
      tw: '台灣',
      us: '美國',
    },
  },
  'zh-CN': {
    title: '嗨，欢迎来到无界选物 👋',
    message: '看起来你目前位于 {{ country }}\n\n要切换至{{ store }}站吗？\n我们会为你显示当地适用的价格、配送与商品信息。',
    cancel: '留在目前站点',
    change: '前往{{ store }}站',
    stores: {
      tw: '台湾',
      us: '美国',
    },
  },
};

/**
 * ISO country handle → modal locale. Unlisted countries use English.
 *
 * @type {Record<string, string>}
 */
const WUU_COUNTRY_MODAL_LOCALES = {
  TW: 'zh-TW',
  HK: 'zh-TW',
  MO: 'zh-TW',
  CN: 'zh-CN',
};

/**
 * First-visit store suggestion modal.
 *
 * Visitor country comes from Shopify browsing_context_suggestions (IP).
 * Store mapping comes from rendered country-selector items, not a JSON script.
 * Copy follows the detected country, not the current storefront locale.
 *
 * @extends DialogComponent
 */
class WuuCountryModal extends DialogComponent {
  /**
   * Dialog element required by DialogComponent.
   *
   * @type {string[]}
   */
  requiredRefs = ['dialog'];

  /**
   * localStorage flag written after the visitor dismisses or follows the prompt.
   * Once set, the modal is not shown again in this browser.
   *
   * @type {string}
   */
  requiredKey = '_wuu_country_modal';

  /**
   * When true, skip IP detection and always open the dialog (theme/dev use).
   *
   * @type {boolean}
   */
  requiredDebug = false;

  /**
   * Bind close handling, then decide whether to prompt.
   * No-op in the theme editor. `requiredDebug` opens immediately; otherwise `#open`.
   */
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(DialogCloseEvent.eventName, this.#closed);

    if (window.Shopify?.designMode) return;

    if (this.requiredDebug) {
      this.#prepareDialog();
      this.showDialog();
    } else {
      this.#open();
    }
  }

  /**
   * Remove the close listener before the element leaves the document.
   */
  disconnectedCallback() {
    this.removeEventListener(DialogCloseEvent.eventName, this.#closed);
    super.disconnectedCallback();
  }

  /**
   * Keep the current storefront. Records dismissal so the prompt does not return.
   */
  cancel() {
    this.#mark();
    this.closeDialog();
  }

  /**
   * Switch to the suggested storefront. Records dismissal, then navigates while
   * preserving the current path and query (hash is dropped).
   *
   * @param {MouseEvent} [event]
   */
  changeStore(event) {
    event?.preventDefault();
    this.#mark();

    const url = this.dataset.targetUrl?.trim();
    if (!url) {
      this.closeDialog();
      return;
    }

    try {
      const dest = new URL(url, window.location.origin);
      dest.pathname = window.location.pathname;
      dest.search = window.location.search;
      dest.hash = '';
      window.location.assign(dest.href);
    } catch {
      window.location.assign(url);
    }
  }

  /**
   * Collect storefronts from the header country selector.
   * Items without a country `code` or a real `href` are skipped.
   *
   * @returns {StoreConfig[]}
   */
  #getStores() {
    return Array.from(document.querySelectorAll('.wuu-country-selector__item')).flatMap((item) => {
      const code = item.getAttribute('data-code') || '';
      const current = item.getAttribute('aria-current') === 'true';
      const url = (item.getAttribute('href') || '').trim();
      if (!code || !url || url === '#') return [];
      return [
        {
          code,
          url,
          current,
        },
      ];
    });
  }

  /**
   * @returns {Record<string, string>}
   */
  #getStoreNames() {
    const raw = this.dataset.storeNames;
    if (!raw) return {};
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : {};
    } catch {
      return {};
    }
  }

  /**
   * @param {string} [iso] Shopify `request.locale.iso_code` or a BCP 47 tag.
   * @returns {string}
   */
  #normalizeLocale(iso) {
    const raw = String(iso || '')
      .trim()
      .replaceAll('_', '-');

    if (/^zh-hant/i.test(raw) || /^zh-(tw|hk|mo)$/i.test(raw)) return 'zh-TW';
    if (/^zh-hans/i.test(raw) || /^zh-(cn|sg)$/i.test(raw)) return 'zh-CN';
    if (/^en\b/i.test(raw)) return 'en';
    return raw || 'en';
  }

  /**
   * Storefront language for this page (used to overlay `| t` / editor copy).
   *
   * @returns {string}
   */
  #currentLocale() {
    return this.#normalizeLocale(this.dataset.currentLocale);
  }

  /**
   * Modal language for a detected ISO country handle.
   *
   * @param {string} code
   * @returns {string}
   */
  #localeForCountry(code) {
    const normalized = String(code || '').toUpperCase();
    return WUU_COUNTRY_MODAL_LOCALES[normalized] || 'en';
  }

  /**
   * Copy for a modal locale. Current storefront language is overlaid from
   * `data-*` so Translate & Adapt and theme-editor overrides still apply.
   *
   * @param {string} locale
   * @returns {ModalCopy}
   */
  #getCopy(locale) {
    const bundled = WUU_COUNTRY_MODAL_I18N[locale] || WUU_COUNTRY_MODAL_I18N.en;
    const isCurrent = locale === this.#currentLocale();
    const missing = !WUU_COUNTRY_MODAL_I18N[locale];

    if (!isCurrent && !missing) return bundled;

    const stores = this.#getStoreNames();
    return {
      title: this.#prepareTemplate('titleTemplate') || bundled.title,
      message: this.#prepareTemplate('messageTemplate') || bundled.message,
      cancel: this.#prepareTemplate('cancelTemplate') || bundled.cancel,
      change: this.#prepareTemplate('changeTemplate') || bundled.change,
      stores: {
        ...bundled.stores, 
        ...stores
      },
    };
  }

  /**
   * @param {string} code ISO 3166-1 alpha-2, e.g. `"TW"`.
   * @param {string} locale
   * @param {string} [fallback]
   * @returns {string}
   */
  #getCountryDisplayName(code, locale, fallback = '') {
    const region = String(code || '').toUpperCase();
    if (!/^[A-Z]{2}$/.test(region)) return fallback || region;

    try {
      const name = new Intl.DisplayNames([locale], { type: 'region' }).of(region);
      return name || fallback || region;
    } catch {
      return fallback || region;
    }
  }

  /**
   * @param {string} code
   * @param {Record<string, string>} [storeNames]
   * @returns {string}
   */
  #getStoreDisplayName(code, storeNames) {
    const normalized = String(code || '').toLowerCase();
    const names = storeNames && typeof storeNames === 'object' ? storeNames : this.#getStoreNames();
    if (names[normalized]) return names[normalized];
    const item = document.querySelector(`.wuu-country-selector__item[data-code="${normalized}"]`);
    return (
      item?.getAttribute('data-label') ||
      item?.querySelector('.wuu-country-selector__name')?.textContent?.trim() ||
      code
    );
  }

  /**
   * Hostname of a storefront URL, lowercased, for comparing with `location.hostname`.
   *
   * @param {StoreConfig} store
   * @returns {string} Empty string if `store.url` is not a valid URL.
   */
  #getStoreHost(store) {
    try {
      return new URL(store.url, window.location.origin).hostname.toLowerCase();
    } catch {
      return '';
    }
  }

  /**
   * Visitor country from Shopify IP geolocation (`browsing_context_suggestions`).
   *
   * @returns {Promise<{ code: string, name: string } | null>}
   */
  async #getDetectedCountry() {
    try {
      const root = window.Shopify?.routes?.root || '/';
      const response = await fetch(`${root}browsing_context_suggestions.json?country[enabled]=true`);
      if (!response.ok) return null;
      const data = await response.json();
      const country = data?.detected_values?.country;
      const handle = country?.handle;
      if (typeof handle !== 'string') return null;

      return {
        code: handle.toUpperCase(),
        name: country?.name || handle,
      };
    } catch {
      return null;
    }
  }

  /**
   * Storefront whose `code` matches the visitor country (case-insensitive).
   * Mainland China / Hong Kong / Macao / Taiwan all map to the Taiwan store.
   * If none match, fall back to the first list item.
   *
   * @param {string} code Uppercase ISO country handle from `#getDetectedCountry`.
   * @param {StoreConfig[]} stores
   * @returns {StoreConfig | null}
   */
  #findStore(code, stores) {
    // CN / HK / MO / TW → Taiwan store
    const resolved = ['CN', 'HK', 'MO', 'TW'].includes(code) ? 'TW' : code;
    const store = stores.find((store) => String(store.code || '').toUpperCase() === resolved);
    if (store) return store;
    return stores[0] || null;
  }

  /**
   * Decode Liquid `| json` leftovers that HTML attributes do not parse.
   * Shopify often emits `'` as `&#39;`; `| json` then turns `&` into `\u0026`,
   * so dataset readers see the literal `\\u0026#39;` instead of `'`.
   *
   * @param {string} value
   * @returns {string}
   */
  #decodeTemplate(value) {
    return String(value || '')
      .replace(/\\u([0-9a-fA-F]{4})/g, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/&#x([0-9a-fA-F]+);/gi, (_, hex) => String.fromCharCode(parseInt(hex, 16)))
      .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(Number(code)))
      .replace(/&apos;/g, "'")
      .replace(/&quot;/g, '"')
      .replace(/&amp;/g, '&')
      .replace(/\\n/g, '\n');
  }

  /**
   * Read a template from `data-*` and decode JSON / HTML entity escapes.
   *
   * @param {string} name camelCase dataset key, e.g. `"messageTemplate"`.
   * @returns {string}
   */
  #prepareTemplate(name) {
    const value = this.dataset[name];
    return typeof value === 'string' ? this.#decodeTemplate(value) : '';
  }

  /**
   * @param {string} content
   * @param {{ country: string, store: string }} values
   * @returns {string}
   */
  #prepareInterpolate(content, values) {
    return String(content || '')
      .replaceAll('{{ country }}', values.country)
      .replaceAll('{{ store }}', values.store);
  }

  /**
   * @param {StoreConfig} store
   * @param {string} countryCode
   * @param {string} locale
   * @param {string} [countryFallback]
   */
  #prepareText(store, countryCode, locale, countryFallback = '') {
    const title = this.querySelector('#wuu-country-modal-title');
    const message = this.querySelector('#wuu-country-modal-message');
    const cancelButton = this.querySelector('.wuu-country-modal__button--cancel');
    const changeButton = this.querySelector('.wuu-country-modal__button--change');
    const copy = this.#getCopy(locale);
    const storeValues = {
      country: this.#getCountryDisplayName(countryCode, locale, countryFallback),
      store: this.#getStoreDisplayName(store.code, copy.stores),
    };

    const dialog = this.refs?.dialog;
    if (dialog instanceof HTMLElement) {
      dialog.setAttribute('lang', locale);
    }

    if (title instanceof HTMLElement) {
      title.textContent = this.#prepareInterpolate(copy.title, storeValues);
    }
    if (message instanceof HTMLElement) {
      message.textContent = this.#prepareInterpolate(copy.message, storeValues);
    }
    if (cancelButton instanceof HTMLButtonElement) {
      cancelButton.textContent = this.#prepareInterpolate(copy.cancel, storeValues);
    }
    if (changeButton instanceof HTMLButtonElement) {
      changeButton.textContent = this.#prepareInterpolate(copy.change, storeValues);
    }
  }

  /**
   * Populate copy for debug mode or when country data is unavailable.
   */
  #prepareDialog() {
    const stores = this.#getStores();
    if (!stores.length) return;
    const store = stores.find((store) => !store.current) || stores[0];
    if (!store) return;
    if (store?.url) {
      this.dataset.targetUrl = store.url;
    }
    this.#prepareText(store, store.code, this.#localeForCountry(store.code));
  }

  /**
   * Open the prompt when the visitor has not dismissed it, storefronts exist,
   * IP country is known, and the matched store is not already the current one.
   *
   * @returns {Promise<void>}
   */
  async #open() {
    if (this.#isMarked()) return;

    const stores = this.#getStores();
    if (!stores.length) return;

    const detected = await this.#getDetectedCountry();
    if (!detected) return;

    const store = this.#findStore(detected.code, stores);
    if (!store?.url) return;

    const current = stores.find((store) => store.current) || null;
    const currentHost = window.location.hostname.toLowerCase();

    if (current && store.code && current.code === store.code) return;
    if (current && store.url && current.url === store.url) return;
    if (this.#getStoreHost(store) === currentHost) return;

    this.dataset.targetUrl = store.url;
    this.#prepareText(store, detected.code, this.#localeForCountry(detected.code), detected.name);
    this.showDialog();
  }

  /**
   * Dialog close (backdrop, Escape, etc.): same as Cancel — do not prompt again.
   */
  #closed = () => {
    this.#mark();
  };

  /**
   * Whether this browser already dismissed or followed the prompt.
   *
   * @returns {boolean} `false` if localStorage is unavailable.
   */
  #isMarked() {
    try {
      return localStorage.getItem(this.requiredKey) === '1';
    } catch {
      return false;
    }
  }

  /**
   * Persist dismissal in localStorage. Failures (private mode, quota) are ignored.
   */
  #mark() {
    try {
      localStorage.setItem(this.requiredKey, '1');
    } catch {
      // Private mode can throw; ignore.
    }
  }

  /**
   * Clear the dismissal flag so the prompt can show again (debug / reset).
   */
  #unmark() {
    try {
      localStorage.removeItem(this.requiredKey);
    } catch {
      // Private mode can throw; ignore.
    }
  }
}

if (!customElements.get('wuu-country-modal')) {
  customElements.define('wuu-country-modal', WuuCountryModal);
}
